import { useMemo } from "react";

import type { TcTechnicalSummaryProjection } from "../../types/tcTechnicalSummary";
import { formatDateOnlyForDisplay, parseDateOnly } from "../../utils/dateOnly";
import { paginateTcTechnicalSummary } from "../../utils/tcTechnicalSummaryPagination";
import "./TcTechnicalSummaryDocument.css";

interface Props {
  projection: TcTechnicalSummaryProjection;
}

function formatTcDateOnly(value: string | null): string {
  const dateOnly = parseDateOnly(value);
  return dateOnly ? formatDateOnlyForDisplay(dateOnly) : "—";
}

export function TcTechnicalSummaryDocument({ projection }: Props) {
  const pages = useMemo(
    () => paginateTcTechnicalSummary(projection),
    [projection]
  );
  const cptSummary = useMemo(
    () =>
      projection.cptTotals.length
        ? projection.cptTotals
            .map(({ code, quantity }) => `${code} x${quantity}`)
            .join(", ")
        : "—",
    [projection.cptTotals]
  );
  const dateOfBirth = useMemo(
    () => formatTcDateOnly(projection.patient.dateOfBirth),
    [projection.patient.dateOfBirth]
  );
  const dateCollected = useMemo(
    () => formatTcDateOnly(projection.dates.collected),
    [projection.dates.collected]
  );
  const dateReceived = useMemo(
    () => formatTcDateOnly(projection.dates.received),
    [projection.dates.received]
  );

  return (
    <div className="tc-summary-root">
      {pages.map((page, pageIndex) => (
        <section
          className="tc-summary-page"
          key={`${projection.caseId}-page-${pageIndex + 1}`}
        >
          <div className="tc-summary-header">
            <div>
              <div>Phone: {projection.facility.phone || "—"}</div>
              <div>Fax: {projection.facility.fax || "—"}</div>
              <div>{projection.facility.medicalDirector}</div>
            </div>
            <div className="tc-summary-header-center">
              {projection.facility.logoDataUri ? (
                <img
                  className="tc-summary-logo"
                  src={projection.facility.logoDataUri}
                  alt={projection.facility.name}
                />
              ) : (
                <div className="tc-summary-brand">
                  {projection.facility.name}
                </div>
              )}
            </div>
            <div className="tc-summary-header-right">
              <div>{projection.facility.address || "—"}</div>
              <div>CLIA #: {projection.facility.clia}</div>
            </div>
          </div>
          <div className="tc-summary-title">Technical Summary</div>

          {page.first ? (
            <>
              <div className="tc-summary-meta">
                <div>
                  <span className="tc-summary-meta-label">Patient Name</span>
                  {projection.patient.name || "—"}
                </div>
                <div>
                  <span className="tc-summary-meta-label">DoB</span>
                  {dateOfBirth}
                </div>
                <div>
                  <span className="tc-summary-meta-label">External ID</span>
                  {projection.externalCaseNumber || "—"}
                </div>
                <div>
                  <span className="tc-summary-meta-label">GCP Case Number</span>
                  {projection.caseNumber}
                </div>
              </div>
              <div className="tc-summary-detail-grid">
                <span className="tc-summary-detail-label">Gender</span>
                <span>{projection.patient.gender || "—"}</span>
                <span className="tc-summary-detail-label">Date Collected</span>
                <span>{dateCollected}</span>
                <span className="tc-summary-detail-label">MRN</span>
                <span>{projection.patient.medicalRecordNumber || "—"}</span>
                <span className="tc-summary-detail-label">Date Received</span>
                <span>{dateReceived}</span>
                <span className="tc-summary-detail-label">Ordering</span>
                <span>{projection.physician.name || "—"}</span>
                <span className="tc-summary-detail-label">Phone</span>
                <span>{projection.physician.phone || "—"}</span>
                <span className="tc-summary-detail-label">Treating</span>
                <span>{projection.physician.name || "—"}</span>
                <span className="tc-summary-detail-label">Fax</span>
                <span>{projection.physician.fax || "—"}</span>
              </div>
              <div className="tc-summary-clinical">
                <strong>Clinical Information:</strong>{" "}
                {projection.clinicalInformation || "—"}
              </div>
            </>
          ) : (
            <div className="tc-summary-identity">
              {projection.patient.name || "Unknown patient"} ·{" "}
              {projection.caseNumber}
            </div>
          )}

          {page.groups.map((group) => (
            <div className="tc-summary-group" key={group.id}>
              <div className="tc-summary-group-heading">
                <div>
                  {group.materialDescription}
                  <br />
                  Tissue: {projection.caseNumber} {group.tissueLabel}
                  {group.continued ? " (continued)" : ""}
                </div>
                <div className="tc-summary-source">
                  {group.sourceDescription || "—"}
                </div>
              </div>
              <div className="tc-summary-stain-header">
                <div>Stain Name</div>
                <div>CPT Code</div>
              </div>
              {group.stains.map((stain, stainIndex) => (
                <div
                  className="tc-summary-stain-row"
                  key={`${group.id}-${stainIndex}`}
                >
                  <div>{stain.stainName}</div>
                  <div>{stain.cptCode || "—"}</div>
                </div>
              ))}
            </div>
          ))}

          {page.final && (
            <div className="tc-summary-final">
              <strong>Comments:</strong>
              <div className="tc-summary-comments-line" />
              <div className="tc-summary-comments-line" />
              <div className="tc-summary-qc">
                <div>Quality Control Review by: __________________</div>
                <div>Date: ______________</div>
              </div>
              <div className="tc-summary-cpt">
                CPT Codes: &nbsp;&nbsp; {cptSummary}
              </div>
            </div>
          )}
          {page.final && (
            <div className="tc-summary-disclaimer">{projection.disclaimer}</div>
          )}
          <div className="tc-summary-page-number">
            Page {pageIndex + 1} of {pages.length}
          </div>
        </section>
      ))}
    </div>
  );
}
